import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert
} from '@mui/material';
import { ErrorOutline, PersonOff, Lock } from '@mui/icons-material';
import { Link } from 'react-router-dom';

const LoginErrorModal = ({ open, onClose, error, isMobile = false }) => {
  const getErrorDetails = (errorMessage) => {
    if (errorMessage.includes('Account is deactivated') || errorMessage.includes('deactivated')) {
      return {
        icon: <PersonOff sx={{ fontSize: 48, color: 'var(--color-error)' }} />,
        title: 'Account Deactivated',
        message: 'Your account has been deactivated. Please contact your administrator for assistance.',
        severity: 'warning'
      };
    } else if (errorMessage.includes('Invalid email or password') || errorMessage.includes('Invalid')) {
      return {
        icon: <Lock sx={{ fontSize: 48, color: 'var(--color-error)' }} />,
        title: 'Invalid Credentials',
        message: 'The email address or password you entered is incorrect. Please check your credentials and try again.',
        severity: 'error'
      };
    } else {
      return {
        icon: <ErrorOutline sx={{ fontSize: 48, color: 'var(--color-error)' }} />,
        title: 'Login Error',
        message: errorMessage || 'An unexpected error occurred. Please try again.',
        severity: 'error'
      };
    }
  };

  const errorDetails = getErrorDetails(error);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: 'var(--color-bg-alt)',
          border: '1px solid var(--color-border)',
          borderRadius: '12px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
        }
      }}
    >
      <DialogTitle sx={{ 
        textAlign: 'center', 
        pb: 1,
        color: 'var(--color-text)',
        fontSize: isMobile ? '20px' : '24px',
        fontWeight: 600
      }}>
        {errorDetails.title}
      </DialogTitle>
      
      <DialogContent sx={{ textAlign: 'center', pt: 1 }}>
        <Box sx={{ mb: 3 }}>
          {errorDetails.icon}
        </Box>
        
        <Alert 
          severity={errorDetails.severity} 
          sx={{ 
            mb: 2,
            background: errorDetails.severity === 'warning' 
              ? 'rgba(255, 152, 0, 0.1)' 
              : 'rgba(244, 67, 54, 0.1)',
            border: errorDetails.severity === 'warning'
              ? '1px solid rgba(255, 152, 0, 0.3)'
              : '1px solid rgba(244, 67, 54, 0.3)',
            color: 'var(--color-text)',
            '& .MuiAlert-icon': {
              color: errorDetails.severity === 'warning' 
                ? 'var(--color-warning)' 
                : 'var(--color-error)'
            }
          }}
        >
          <Typography variant="body1" sx={{ 
            color: 'var(--color-text)',
            fontSize: isMobile ? '14px' : '16px',
            lineHeight: 1.5
          }}>
            {errorDetails.message}
          </Typography>
        </Alert>
      </DialogContent>
      
      <DialogActions sx={{ justifyContent: 'center', pb: 3, px: 3, gap: 1, flexWrap: 'wrap' }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            background: 'var(--color-accent)',
            color: 'white',
            px: 4,
            py: 1,
            borderRadius: '8px',
            fontSize: isMobile ? '16px' : '14px',
            fontWeight: 600,
            textTransform: 'none',
            minHeight: isMobile ? '48px' : '40px',
            '&:hover': {
              background: 'var(--color-accent-hover)',
              transform: 'translateY(-1px)',
              boxShadow: '0 8px 25px rgba(33, 150, 243, 0.3)',
            }
          }}
        >
          Try Again
        </Button>
        {errorDetails.title === 'Invalid Credentials' && (
          <Button
            component={Link}
            to="/forgot-password"
            variant="text"
            sx={{
              color: 'var(--color-accent)',
              textTransform: 'none',
              fontWeight: 600,
              minHeight: isMobile ? '44px' : '40px',
            }}
          >
            Forgot password?
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default LoginErrorModal;
